import { useState } from 'react';
import {
  BarChart3, Bell, Building2, CalendarDays, CheckCircle2, CreditCard, FileBarChart, Flame,
  HandCoins, History, Inbox, Landmark, LayoutDashboard, Link2, Mail, Mic2, NotebookText, Receipt, Repeat, ScrollText,
  Settings, ShoppingBag, Star, UserRound, UserCog, Users, Wallet, Scale, Pointer, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * 3D icons generated by `npm run icons` from LOGO/*.png into /public/icons/3d/{name}-{size}.webp.
 * If a file is missing (or fails to load) a plain line icon is shown instead.
 */
export const ICON3D_FALLBACK = {
  'app-logo': Building2,
  'logo-mark': Building2,
  'hero-building': Building2,
  'facade-banner': Building2,
  dashboard: LayoutDashboard,
  people: Users,
  'person-card': UserRound,
  ledger: NotebookText,
  payments: CreditCard,
  'pay-link': Link2,
  cashbox: Wallet,
  expenses: Receipt,
  bank: Landmark,
  'bank-match': Scale,
  nedarim: HandCoins,
  keva: Repeat,
  alerts: Bell,
  minyanim: Building2,
  torah: ScrollText,
  yad: Pointer,
  sales: ShoppingBag,
  chazzan: Mic2,
  calendar: CalendarDays,
  yahrzeit: Flame,
  'bar-mitzvah': Star,
  mailing: Mail,
  reports: BarChart3,
  'annual-report': FileBarChart,
  users: UserCog,
  audit: History,
  settings: Settings,
  'empty-state': Inbox,
  success: CheckCircle2,
} satisfies Record<string, LucideIcon>;

export type Icon3DName = keyof typeof ICON3D_FALLBACK;

const SIZES = [32, 48, 64, 128, 256] as const;
const LARGE: Partial<Record<Icon3DName, number[]>> = {
  'hero-building': [512, 1024], 'facade-banner': [512, 1024], 'logo-mark': [512], 'empty-state': [512], success: [512],
};

function fileSize(name: Icon3DName, px: number): number {
  const all = [...SIZES, ...(LARGE[name] ?? [])].sort((a, b) => a - b);
  return all.find((s) => s >= px) ?? all[all.length - 1];
}

export function icon3dUrl(name: Icon3DName, px: number): string {
  return `/icons/3d/${name}-${fileSize(name, px)}.webp`;
}

export function Icon3D({ name, size = 32, className, alt = '', eager }: {
  name: Icon3DName; size?: number; className?: string; alt?: string; eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    const Fallback = ICON3D_FALLBACK[name] ?? Inbox;
    return <Fallback className={cn('shrink-0 text-primary', className)} style={{ width: size, height: size }} aria-hidden={!alt} aria-label={alt || undefined} />;
  }
  return (
    <img
      src={icon3dUrl(name, size)}
      srcSet={`${icon3dUrl(name, size)} 1x, ${icon3dUrl(name, size * 2)} 2x`}
      width={size}
      height={size}
      alt={alt}
      aria-hidden={!alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable={false}
      onError={() => setFailed(true)}
      className={cn('shrink-0 select-none object-contain', className)}
    />
  );
}
